import { useEffect, useRef, useState, FormEvent } from "react";

type Msg = { from: "them" | "me"; text: string; time: string };

const INITIAL: Msg[] = [
  { from: "them", text: "Hello — welcome to Aurelin. How can we help you today?", time: "Just now" },
];

const QUICK = ["Sizing help", "Shipping & delivery", "Order status"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>(INITIAL);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = (text: string) => {
    const t = text.trim();
    if (!t) return;
    const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    setMsgs((prev) => [...prev, { from: "me", text: t, time: now }]);
    setDraft("");
    setTimeout(() => {
      setMsgs((prev) => [
        ...prev,
        {
          from: "them",
          text: "Thanks for the note — our team will reply shortly. In the meantime, feel free to browse the collection.",
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        },
      ]);
    }, 900);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    send(draft);
  };

  return (
    <>
      {/* Bubble button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-6 right-6 z-[85] w-14 h-14 aspect-square shrink-0 rounded-full flex items-center justify-center text-canvas transition-all duration-200 hover:opacity-90"
        style={{
          backgroundColor: "#1E144D",
          boxShadow: "0 8px 24px rgba(30,20,77,0.28)",
        }}
      >
        <i className={`ri-${open ? "close" : "chat-3"}-line text-[22px]`}></i>
      </button>

      {/* Panel */}
      <div
        className="fixed bottom-24 right-6 z-[84] w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-8rem)] bg-canvas overflow-hidden flex flex-col"
        style={{
          borderRadius: 18,
          boxShadow: "0 20px 60px rgba(30,20,77,0.22)",
          transformOrigin: "bottom right",
          transition: "opacity 240ms cubic-bezier(0.22,1,0.36,1), transform 240ms cubic-bezier(0.22,1,0.36,1)",
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0) scale(1)" : "translateY(12px) scale(0.98)",
          pointerEvents: open ? "auto" : "none",
        }}
        role="dialog"
        aria-label="Customer support chat"
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-5" style={{ backgroundColor: "#1E144D" }}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[15px] font-medium text-canvas tracking-wide">AURELIN CARE</p>
              <p className="mt-1 flex items-center gap-2 text-[13px] text-canvas/80">
                <span className="w-2 h-2 rounded-full bg-green-400 inline-block"></span>
                We'll reply as soon as we can
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="w-8 h-8 flex items-center justify-center cursor-pointer text-canvas/80 hover:text-canvas transition-colors"
            >
              <i className="ri-close-line text-[18px]"></i>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-3 bg-canvas">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] px-4 py-3 text-[14px] leading-relaxed ${
                  m.from === "me" ? "text-canvas" : "text-black bg-[#F2F2F2]"
                }`}
                style={{
                  borderRadius: 14,
                  backgroundColor: m.from === "me" ? "#1E144D" : undefined,
                }}
              >
                {m.text}
              </div>
            </div>
          ))}

          {msgs.length <= 1 && (
            <div className="pt-2 flex flex-wrap gap-2">
              {QUICK.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="px-3 py-2 text-[12px] border border-black/15 text-graphite hover:border-[#1E144D] hover:text-[#1E144D] cursor-pointer transition-colors"
                  style={{ borderRadius: 999 }}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Input */}
        <form onSubmit={onSubmit} className="border-t border-black/10 px-4 py-3 flex items-center gap-2 bg-canvas">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write your message…"
            className="flex-1 h-10 bg-transparent outline-none text-[14px] text-black placeholder:text-graphite"
          />
          <button
            type="button"
            aria-label="Emoji"
            className="w-9 h-9 flex items-center justify-center text-graphite hover:text-black cursor-pointer transition-colors"
          >
            <i className="ri-emotion-line text-[18px]"></i>
          </button>
          <button
            type="submit"
            aria-label="Send"
            className="w-9 h-9 flex items-center justify-center text-canvas cursor-pointer transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#1E144D", borderRadius: 999 }}
          >
            <i className="ri-send-plane-fill text-[14px]"></i>
          </button>
        </form>
      </div>
    </>
  );
}